import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Calculatrice de consommation quotidienne de café - Calcul de la dose sûre de caféine";
const DESCRIPTION =
  "Calculatrice de consommation quotidienne de café - Calcul de la dose sûre de caféine en francais pour estimer vos indicateurs de sante rapidement. Entrez vos donnees et obtenez un resultat clair avec des reperes utiles pour le suivi quotidien.";
const KEYWORDS =
  "de consommation quotidienne de café calcul de la dose sûre de caféine, calculatrice de consommation quotidienne de café calcul de la dose sûre de caféine, calcul en ligne, sante, bien-etre, indicateurs sante, resultat rapide";
const OG_URL = "https://do-calculate.com/calculator/fr/coffee-caffeine/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Calculatrice de consommation quotidienne de café",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "HealthApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
];

const BEVERAGES = {
  espresso: { name: "Espresso (30 ml)", caffeine: 63, unit: "tasse" },
  latte: { name: "Latte / Cappuccino (240 ml)", caffeine: 77, unit: "tasse" },
  brewed: { name: "Café filtre / Américain (240 ml)", caffeine: 96, unit: "tasse" },
  instant: { name: "Café instantané (240 ml)", caffeine: 62, unit: "tasse" },
  turkish: { name: "Café turc (60 ml)", caffeine: 50, unit: "tasse" },
  french: { name: "Café français (tasse petite)", caffeine: 15, unit: "tasse" },
  black_tea: { name: "Thé noir (240 ml)", caffeine: 47, unit: "tasse" },
  green_tea: { name: "Thé vert (240 ml)", caffeine: 28, unit: "tasse" },
  energy_drink: { name: "Boisson énergétique (250 ml)", caffeine: 80, unit: "canette" },
  cola: { name: "Boisson gazeuse Cola (355 ml)", caffeine: 34, unit: "canette" },
};

function CoffeeCaffeineCalculator() {
  const [weight, setWeight] = useState("");
  const [beverageType, setBeverageType] = useState("espresso");
  const [dailyCups, setDailyCups] = useState(1);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculateCaffeine = () => {
    try {
      setError("");
      const w = parseFloat(weight);
      const cups = parseFloat(dailyCups);

      if (!w || w <= 0) {
        setResult(null);
        return;
      }

      const safeLimit = 400;
      const selectedBeverage = BEVERAGES[beverageType];
      const caffeinePerCup = selectedBeverage.caffeine;
      const totalConsumed = caffeinePerCup * cups;

      const cupsToLimit = Math.floor(safeLimit / caffeinePerCup);
      const lethalDoseMg = w * 150;
      const cupsToLethal = Math.floor(lethalDoseMg / caffeinePerCup);

      let status = "";
      let statusColor = "";
      if (totalConsumed <= 200) {
        status = "Consommation sûre et faible";
        statusColor = "#1a5f3f";
      } else if (totalConsumed <= 400) {
        status = "Consommation modérée (dans les limites recommandées)";
        statusColor = "#856404";
      } else {
        status = "Consommation élevée (a dépassé la limite quotidienne sûre)";
        statusColor = "#721c24";
      }

      setResult({
        totalConsumed,
        safeLimit,
        cupsToLimit,
        cupsToLethal,
        status,
        statusColor,
        beverageName: selectedBeverage.name,
        unit: selectedBeverage.unit,
      });
    } catch (err) {
      setError("Une erreur s'est produite lors du calcul. Veuillez vérifier vos données.");
    }
  };

  useEffect(() => {
    calculateCaffeine();
  }, [weight, beverageType, dailyCups]);

  const handleClear = () => {
    setWeight("");
    setBeverageType("espresso");
    setDailyCups(1);
    setResult(null);
    setError("");
  };

  return (
    <div className="ltr">
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>Calculatrice de consommation quotidienne de café</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                La <strong>calculatrice de consommation de café</strong> vous aide à suivre la quantité de caféine que vous consommez quotidiennement. 
                La FDA recommande que les adultes en bonne santé ne dépassent pas <strong>400 mg</strong> de caféine par jour. 
                Entrez votre poids et le type de boisson que vous préférez pour connaître vos limites sûres.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>Données de consommation</h2>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label>Poids corporel (kg)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    placeholder="Exemple : 70"
                  />
                </div>
                <div className="col-md-6">
                  <label>Type de boisson</label>
                  <select
                    className="form-select"
                    value={beverageType}
                    onChange={(e) => setBeverageType(e.target.value)}
                  >
                    {Object.entries(BEVERAGES).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label>Nombre de tasses/canettes par jour</label>
                  <input
                    type="number"
                    className="form-control"
                    value={dailyCups}
                    onChange={(e) => setDailyCups(e.target.value)}
                    min="1"
                  />
                </div>
              </div>

              <div className="text-center mb-3 mt-4">
                <button
                  onClick={handleClear}
                  className="btn btn-secondary"
                  style={{ fontSize: "16px", padding: "10px 30px" }}
                >
                  Réinitialiser les données
                </button>
              </div>

              {error && (
                <div className="alert alert-warning" role="alert">
                  {error}
                </div>
              )}

              {result && (
                <div className="mt-4">
                  <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}>
                    <strong>Résultats :</strong>
                  </h3>
                  <table className="table bmi-table" style={{ width: "100%" }}>
                    <thead>
                      <tr>
                        <th className="bmi-header" style={{ textAlign: "center" }}>Paramètre</th>
                        <th className="bmi-header" style={{ textAlign: "center" }}>Valeur</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ textAlign: "center" }}>Total de caféine consommée</td>
                        <td style={{ textAlign: "center", fontWeight: "bold", color: result.statusColor }}>
                          {result.totalConsumed} mg
                        </td>
                      </tr>
                      <tr>
                        <td style={{ textAlign: "center" }}>Statut</td>
                        <td style={{ textAlign: "center", fontWeight: "bold", color: result.statusColor }}>
                          {result.status}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ textAlign: "center" }}>Limite maximale sûre par jour</td>
                        <td style={{ textAlign: "center" }}>400 mg</td>
                      </tr>
                      <tr>
                        <td style={{ textAlign: "center" }}>Nombre de {result.unit}s autorisées pour éviter les symptômes</td>
                        <td style={{ textAlign: "center", fontWeight: "bold" }}>
                          {result.cupsToLimit} {result.unit}s
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <div className="info-sections-container mt-4">
                    <div className="info-section">
                      <div className="info-section-header"><strong>Avertissement concernant les doses dangereuses</strong></div>
                      <div className="info-section-body">
                        <p>En fonction de votre poids ({weight} kg), la quantité de caféine pourrait devenir très dangereuse ou mortelle si vous atteigniez :</p>
                        <h4 className="text-danger text-center">{result.cupsToLethal} {result.unit}s</h4>
                        <p className="small text-muted text-center">* Ce chiffre est approximatif à titre éducatif uniquement, la sensibilité à la caféine varie d'une personne à l'autre.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <hr />
            <RelatedLinks />
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Conseils d'utilisation</h3></div>
              <div className="info-section-body">
                <p>Pour obtenir un resultat pertinent, verifiez vos unites, saisissez des valeurs realistes et utilisez ce calcul comme aide a la decision, puis adaptez selon votre situation.</p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>Guide de consommation sûre de caféine</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  La caféine est une substance stimulante naturelle présente dans le café, le thé et le chocolat. Bien qu'elle offre de la vigilance et de l'énergie, l'excès en entraîne des effets secondaires.
                </p>
                <h3 style={{ fontSize: "17px" }}>Limites recommandées (selon la FDA) :</h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>Adultes en bonne santé :</strong> 400 mg maximum (équivalent à 4 tasses de café filtre).</li>
                  <li><strong>Femmes enceintes et allaitantes :</strong> La consommation ne doit pas dépasser 200 mg par jour.</li>
                  <li><strong>Enfants et adolescents :</strong> Il est conseillé de réduire la consommation au minimum (pas plus de 2,5 mg par kg de poids corporel).</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Symptômes de surdose de caféine</strong>
              </div>
              <div className="info-section-body">
                <p>Si vous dépassez la limite autorisée, vous pourriez ressentir les symptômes suivants :</p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Insomnie et difficulté à dormir.</li>
                  <li>Accélération du rythme cardiaque.</li>
                  <li>Anxiété et nervosité.</li>
                  <li>Troubles gastro-intestinaux.</li>
                  <li>Maux de tête et tremblements.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Contenu en caféine des boissons courantes</strong>
              </div>
              <div className="info-section-body">
                <table className="table bmi-table">
                  <thead>
                    <tr>
                      <th className="bmi-header" style={{ textAlign: "center" }}>Boisson</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>Quantité de caféine (environ)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td style={{ textAlign: "center" }}>Tasse de café français</td><td style={{ textAlign: "center" }}>15 mg</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Tasse de café américain (240 ml)</td><td style={{ textAlign: "center" }}>95-165 mg</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Shot d'espresso (30 ml)</td><td style={{ textAlign: "center" }}>63 mg</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Tasse de thé noir</td><td style={{ textAlign: "center" }}>47 mg</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Canette de boisson énergétique</td><td style={{ textAlign: "center" }}>80 mg</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Questions fréquemment posées</strong>
              </div>
              <div className="info-section-body">
                <p><strong>Q : Combien de café puis-je boire sans danger ?</strong></p>
                <p>R : La plupart des adultes en bonne santé peuvent consommer jusqu'à 400 mg de caféine par jour en toute sécurité, ce qui équivaut à environ 4 tasses de café filtre.</p>
                <hr />
                <p><strong>Q : Que faire si je consomme trop de caféine ?</strong></p>
                <p>R : Si vous vous sentez nerveux ou si vous avez des palpitations cardiaques, arrêtez la consommation de caféine et buvez beaucoup d'eau. Consultez un médecin si les symptômes persistent.</p>
                <hr />
                <p><strong>Q : La caféine peut-elle affecter mon sommeil ?</strong></p>
                <p>R : Oui, la caféine peut interférer avec le sommeil si elle est consommée 6 heures avant le coucher. Il est préférable de limiter à 1-2 tasses de café le matin.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CoffeeCaffeineCalculator;
